import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import TextEditor from "../TextEditor";
import { TrashIcon, PhotoIcon, PencilIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import ApiCustom from "../../utils/ApiCustom";
import { toast } from "react-toastify";
import EventPDFModal from "./EventPDFModal";
import ImageManagerModal from "./ImageManagerModal";

const hasPermission = (requiredPermission) => {
  const userId = localStorage.getItem("userId");
  if (userId == 1) {
    return true;
  }
  const userPermissions = getUserPermissions();
  return userPermissions.includes(requiredPermission);
};

const getUserPermissions = () => {
  const permissions = localStorage.getItem("permissions");
  return permissions
    ? JSON.parse(permissions)?.map((p) => p.permissionSlug)
    : [];
};

const eventSchema = Yup.object().shape({
  title: Yup.string().trim().required("Tiêu đề không được để trống"),
  topic: Yup.string().trim().required("Chủ đề không được để trống"),
  time: Yup.string().trim().required("Thời gian không được để trống"),
  location: Yup.string().trim().required("Địa điểm không được để trống"),
  qrCode: Yup.string().trim().required("Mã QR không được để trống"),
  postId: Yup.string().trim().required("Thông báo không được để trống"),
});

const EventModal = ({ isOpen, data, eventPostId, onClose }) => {
  const [formData, setFormData] = useState({
    title: "",
    topic: "",
    time: "",
    location: "",
    note: "",
    content: "",
    qrCode: "",
    postId: "",
  });
  const [rows, setRows] = useState([{ time: "", value: "" }]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasNewImage, setHasNewImage] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [posts, setPosts] = useState([]);
  const [editable, setEditable] = useState([]);

  useEffect(() => {
    getNotification();
  }, []);

  useEffect(() => {
    setEditable(
      data != null
        ? hasPermission("EVENT_UPDATE")
        : hasPermission("EVENT_CREATE")
    );
    if (data) {
      setFormData({
        title: data.title,
        topic: data.topic,
        time: data.time,
        location: data.location,
        note: data.note,
        content: data.content,
        qrCode: data.qrCode,
        postId: data.postId,
      });
      setRows(JSON.parse(data.content));
    } else {
      setFormData({
        title: "",
        topic: "",
        time: "",
        location: "",
        note: "",
        content: "",
        qrCode: "",
        postId: eventPostId,
      });
      setRows([{ time: "", value: "" }]);
    }
    setErrors([]);
  }, [data, eventPostId]);

  const getNotification = () => {
    ApiCustom.get("/posts")
      .then((res) => {
        const postData = res.data.data?.data.filter(
          (x) => x.type === "notification"
        );
        setPosts(postData);
      })
      .catch((err) => console.log(err));
  };

  const handleChange = async (e) => {
    const name = e.name || e.target?.name;
    const value = e.value || e.target?.value;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await eventSchema.validate(formData, { abortEarly: false });
      if (
        rows.some((item) => item.time.trim() === "" && item.value.trim() === "")
      ) {
        setErrors((prev) => ({
          ...prev,
          content: "Vui lòng điền đầy đủ nội dung",
        }));
        setIsSubmitting(false);
        return;
      }
      if (data) {
        ApiCustom.put("/event-update/" + data.id, formData, {
          headers: { "Content-Type": "application/json" },
        })
          .then((res) => {
            onClose();
            toast.success("Cập nhật thành công");
            setIsSubmitting(false);
          })
          .catch((err) => console.log(err));
      } else {
        ApiCustom.post("/event-create", formData, {
          headers: { "Content-Type": "application/json" },
        })
          .then((res) => {
            onClose();
            toast.success("Thêm thành công");
            setIsSubmitting(false);
          })
          .catch((err) => console.log(err));
      }
    } catch (error) {
      setIsSubmitting(false);
      if (error instanceof Yup.ValidationError) {
        const formErrors = {};
        error.inner.forEach((err) => {
          formErrors[err.path] = err.message;
        });
        setErrors(formErrors);
      } else {
        toast.error("Đã xảy ra lỗi! Vui lòng thử lại.");
        console.error("Lỗi khi lưu mã thưởng:", error);
      }
    }
  };

  const getImageUrl = () => {
    if (hasNewImage) {
      return imagePreview;
    } else if (data && data.qrCode) {
      return data.qrCode;
    }
    return "";
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handlePressImage = (media) => {
    setIsModalOpen(false);
    setHasNewImage(true);
    const imagePreviewChange = media.url;
    setImagePreview(imagePreviewChange);
    setFormData({ ...formData, qrCode: media.url });
    setErrors({ ...errors, qrCode: "" });
  };

  const handleRowChange = (index, e) => {
    const { name, value } = e.target;
    const updatedRows = [...rows];
    updatedRows[index][name] = value;
    setRows(updatedRows);
    setFormData({ ...formData, content: JSON.stringify(updatedRows) });
    setErrors({ ...errors, content: "" });
  };

  const handleAddRow = () => {
    setRows([...rows, { time: "", value: "" }]);
  };

  const handleDeleteRow = (index) => {
    const updatedRows = rows.filter((_, i) => i !== index);
    setRows(updatedRows);
  };

  if (!isOpen) {
    return;
  }

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-base-100 rounded-lg w-3/4 max-h-[90vh] overflow-y-auto">
        <div className="flex p-6 pb-4 justify-between items-center sticky top-0 bg-base-100 z-10">
          <h2 className="text-xl font-semibold mb-4">Nội dung thiệp mời</h2>
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-base-700 bg-base-100 hover:bg-base-200"
            disabled={isSubmitting}
          >
            X
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {data != null || hasPermission("EVENT_CREATE") ? (
            <div className="px-6">
              <div className="mb-4">
                <label htmlFor="title" className="block text-sm font-medium">
                  Tiêu đề<i className="text-red-600"> *</i>
                </label>
                <input
                  disabled={!editable}
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className={`w-full p-2 border rounded ${
                    errors.title ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.title && (
                  <p className="text-red-500 text-sm mt-1">{errors.title}</p>
                )}
              </div>
              <div className="mb-4">
                <label htmlFor="title" className="block text-sm font-medium">
                  Chủ đề<i className="text-red-600"> *</i>
                </label>
                <input
                  disabled={!editable}
                  type="text"
                  name="topic"
                  value={formData.topic}
                  onChange={handleChange}
                  className={`w-full p-2 border rounded ${
                    errors.topic ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.topic && (
                  <p className="text-red-500 text-sm mt-1">{errors.topic}</p>
                )}
              </div>
              <div className="mb-4">
                <label htmlFor="title" className="block text-sm font-medium">
                  Thời gian<i className="text-red-600"> *</i>
                </label>
                <input
                  disabled={!editable}
                  type="text"
                  name="time"
                  value={formData.time}
                  onChange={handleChange}
                  className={`w-full p-2 border rounded ${
                    errors.time ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.time && (
                  <p className="text-red-500 text-sm mt-1">{errors.time}</p>
                )}
              </div>
              <div className="mb-4">
                <label htmlFor="title" className="block text-sm font-medium">
                  Địa điểm<i className="text-red-600"> *</i>
                </label>
                <TextEditor
                  canUpdate={editable}
                  value={formData.location}
                  onChange={handleChange}
                  name="location"
                />
                {errors.location && (
                  <p className="text-red-500 text-sm mt-1">{errors.location}</p>
                )}
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium  mb-1">
                      Mã QR <span className="text-red-500"> *</span>
                    </label>
                    <div className="mt-1 flex items-center space-x-4">
                      <div
                        className={`relative w-32 h-32 border-2 ${
                          !errors.qrCode ? "border-gray-300" : "border-red-500"
                        } border-dashed rounded-lg overflow-hidden`}
                      >
                        {getImageUrl() ? (
                          <img
                            src={getImageUrl()}
                            alt="Preview"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <PhotoIcon className="w-10 h-10 text-gray-300" />
                          </div>
                        )}

                        {editable && (
                          <PencilSquareIcon
                            className={`h-4 w-4 absolute bottom-1 right-1 cursor-pointer rounded-md`}
                            onClick={() =>  handleOpenModal()
                            }
                          />
                        )}
                      </div>
                    </div>
                    {errors.qrCode && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.qrCode}
                      </p>
                    )}
                  </div>
                  <div className="w-full">
                    <label className="block text-sm font-medium mb-1">
                      Thông báo
                      <i className="text-red-600"> *</i>
                    </label>
                    <select
                      disabled={true}
                      name={formData.postId}
                      value={formData.postId}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          postId: e.target.value,
                        });
                        setErrors({
                          ...errors,
                          postId: "",
                        });
                      }}
                      className={`w-full p-2 border rounded-md ${
                        errors.postId ? "border-red-500" : "border-gray-300"
                      } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    >
                      <option value="">Chọn thông báo</option>
                      {posts?.map((item, idx) => (
                        <option key={idx} value={item.id}>
                          {item.title}
                        </option>
                      ))}
                    </select>
                    {errors.postId && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.postId}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="mb-4 mt-4">
                <label htmlFor="title" className="block text-sm font-medium">
                  Nội dung<i className="text-red-600"> *</i>
                </label>
                <div
                  className={` p-3 border mt-1 ${
                    errors.content ? "border-red-500" : ""
                  }`}
                >
                  {rows.map((row, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center space-x-4"
                    >
                      <div className="w-8 text-center font-bold text-base">
                        {index + 1}
                      </div>
                      <div className="mb-4 flex-1 basis-1/3">
                        <label className="block text-sm font-medium">
                          Thời gian
                        </label>
                        <input
                          disabled={!editable}
                          type="text"
                          name="time"
                          value={row.time}
                          onChange={(e) => handleRowChange(index, e)}
                          className="w-full p-2 border rounded border-gray-300"
                        />
                      </div>
                      <div className="mb-4 flex-1 basis-2/3">
                        <label className="block text-sm font-medium">
                          Nội dung
                        </label>
                        <input
                          disabled={!editable}
                          type="text"
                          name="value"
                          value={row.value}
                          onChange={(e) => handleRowChange(index, e)}
                          className="w-full p-2 border rounded border-gray-300"
                        />
                      </div>
                      <button
                        type="button"
                        disabled={!editable}
                        onClick={() => handleDeleteRow(index)}
                        className="h-10 px-3 btn btn-outline rounded text-red-500 btn-sm hover:text-red-500"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                  <div className="w-full flex justify-end">
                    <button
                      type="button"
                      disabled={!editable}
                      onClick={handleAddRow}
                      className="mt-4 px-4 py-2 bg-base-300 text-base rounded hover:bg-base-200"
                    >
                      Thêm dòng
                    </button>
                  </div>
                </div>
                {errors.content && (
                  <p className="mt-1 text-sm text-red-600">{errors.content}</p>
                )}
              </div>

              <div className="mb-4 mt-4">
                <label htmlFor="title" className="block text-sm font-medium">
                  Ghi chú
                </label>
                <TextEditor
                  canUpdate={editable}
                  value={formData.note}
                  onChange={handleChange}
                  name="note"
                />
                {/* <textarea
              name="note"
              value={formData.note}
              onChange={handleChange}
              className={`w-full p-2 border rounded ${
                errors.note ? "border-red-500" : "border-gray-300"
              }`}
              rows="4"
            ></textarea> */}
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-500 mt-2">Chưa có sự kiện</p>
          )}

          <div className="flex p-6 justify-end space-x-4 sticky bottom-0 bg-base-100 z-10">
            <div className="space-x-5">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 btn btn-sm rounded"
                disabled={isSubmitting}
              >
                {(hasPermission("EVENT_CREATE") && data == null) ||
                (hasPermission("EVENT_UPDATE") && data != null)
                  ? "Hủy"
                  : "Đóng"}
              </button>
              {((hasPermission("EVENT_CREATE") && data == null) ||
                (hasPermission("EVENT_UPDATE") && data != null)) && (
                <button
                  type="submit"
                  className={`px-4 py-2 btn btn-primary rounded btn-sm ${
                    isSubmitting ? "loading" : ""
                  }`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="loading loading-spinner loading-xs mr-2"></span>
                      Đang lưu...
                    </>
                  ) : (
                    "Lưu"
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      {isModalOpen && (
        <ImageManagerModal
          onClose={handleCloseModal}
          onPress={handlePressImage}
          multiImage={true}
          selectedMultiMedia={[]}
          deActiveInsertImage={true}
        />
      )}
    </div>
  );
};

export default EventModal;
